import { cx } from '@emotion/css';
import { styled } from "@mui/material";
import * as React from "react";

//////////////////////////////////////////////////////////////////////

export const INSIDE_LAYOUT_CONTAINER_ID = 'insideContainer';
export const INSIDE_LAYOUT_CONTENT_CONTAINER_ID = 'insideContent';
export const INSIDE_LAYOUT_SUBHEADER_CONTAINER_ID = 'insideSubheader';
export const INSIDE_LAYOUT_LATERAL_MENU_CONTAINER_ID = 'insideLateralMenu';
export const FEED_CONTAINER_SCROLL_TOP_ID = "feedContainerScrollTop";
export const FEED_EXTERNAL_FILTER_ID = "feedExternalFilter";
export const INSIDE_BODY_CONTAINER_ID = "insideBodyContainer";

//////////////////////////////////////////////////////////////////////

const Container = styled('div')(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "214px auto",
    gridTemplateRows: "var(--subheader-height) auto",
    gridTemplateAreas: "'ML SM' 'ML BO'",
    height: "calc(100vh - var(--header-height) - var(--footer-height))",
    position: "relative"
}));

const SubHeaderContainer = styled('div')(({ theme }) => ({
    gridArea: "SM",
    display: "none",
    gridColumnStart: 1,
    backgroundColor: theme.palette.paper.dark,
    height: "var(--subheader-height)",
    paddingRight: theme.spacing(2),
    "&.fix": {
        display: "block",
    },
    [theme.breakpoints.down('md')]: {
        display: "block",
        "&.fix.openmenu": {
            gridColumnStart: 1,
        },
        "&.openmenu": {
            gridColumnStart: 2,
        },
    },
    [theme.breakpoints.up('lg')]: {
        "&.fix": {
            display: "none",
            gridColumnStart: 1,
        },
    }
}));

const LateralMenuContainer = styled('div')(({ theme }) => ({
    gridArea: "ML",
    "a:hover": {
        color: theme.palette.highlight.main,
    },
    "&.fix": {
        display: "none",
        gridRowStart: 2,
        height: "calc(100vh - var(--header-height) - var(--subheader-height) - var(--footer-height))",
    },
    [theme.breakpoints.up('lg')]: {
        "&.fix": {
            display: "block",
            gridRowStart: 1,
            height: "calc(100vh - var(--header-height) - var(--footer-height))",
        },
    },
    [theme.breakpoints.down('md')]: {
        display: "none",
        gridRowStart: 1,
        height: "calc(100vh - var(--header-height) - var(--footer-height))",
    },
    "&.openmenu": {
        display: "block",
    },
    height: "calc(100vh - var(--header-height) - var(--footer-height))",
}));

const ContentContainer = styled(
    'div',
    {
        shouldForwardProp: (propName: string) => propName != "ignoreSubheaderSpace" && propName != "ignoreLateralMenu"
    })<{ ignoreSubheaderSpace: boolean; ignoreLateralMenu: boolean; }>(({ theme, ignoreSubheaderSpace, ignoreLateralMenu }) => ({
        gridArea: "BO",
        gridRowStart: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        //paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2),
        "&.hide": {
            gridColumn: "1 / span 2",
        },
        "&.fix": {
            gridColumn: "1 / span 2",
            gridRowStart: 2,
        },
        [theme.breakpoints.up('lg')]: {
            "&.fix": {
                gridColumn: 2,
                gridRowStart: 1,
            },
        },
        [theme.breakpoints.down('md')]:
            ignoreSubheaderSpace
                ? undefined
                : {
                    gridColumn: "1 / span 2",
                    gridRowStart: 2,
                    "&.hide": {
                        gridRowStart: 1,
                    }
                },
        "&.openmenu": {
            gridColumn: 2,
            "& #feedContainerScrollTop": {
                left: 216
            }
        },
        "#feedContainerScrollTop": {
            position: "absolute",
            bottom: 16,
            left: ignoreLateralMenu ? 8 : 216,
            [theme.breakpoints.down('md')]: {
                left: 8,
            },
        },

        ...theme.extended.workArea.content
    }));

const ContainerBody = styled(
    'div',
    {
        shouldForwardProp: (propName: string) => propName != "overflow" && propName != "padding"
    })<{ overflow: boolean; padding?: number; }>(({ overflow, padding }) => ({
        minWidth: "calc(var(--min-width) - var(--sidebar-width) - 50px)",
        flex: 1,
        padding,
        overflow: overflow == false ? "hidden" : undefined,
        "& .fix": {
            maxWidth: "732px",
        }
    }));

//////////////////////////////////////////////////////////////////////

interface IProps {
    children?: React.ReactNode;
    subHeaderComponent: React.ReactNode;
    lateralMenuComponent: React.ReactNode;
    menuOpened: boolean;
    subHeaderAlwaysVisible: boolean;
    bodyHasOverflow?: boolean;
    bodyPadding?: number;
}


export const MprcLayoutInside: React.FC<IProps> = (props) => {
    const { children, menuOpened, subHeaderAlwaysVisible, subHeaderComponent, lateralMenuComponent, bodyHasOverflow = true, bodyPadding } = props;
    const cls = cx({
        openmenu: menuOpened,
        fix: subHeaderAlwaysVisible,
        hide: lateralMenuComponent == null
    });

    return (
        <Container id={INSIDE_LAYOUT_CONTAINER_ID}>
            {subHeaderComponent && <SubHeaderContainer id={INSIDE_LAYOUT_SUBHEADER_CONTAINER_ID} className={cls}>{subHeaderComponent}</SubHeaderContainer>}
            {lateralMenuComponent && <LateralMenuContainer id={INSIDE_LAYOUT_LATERAL_MENU_CONTAINER_ID} className={cls}>{lateralMenuComponent}</LateralMenuContainer>}
            <ContentContainer id={INSIDE_LAYOUT_CONTENT_CONTAINER_ID} className={cls} ignoreSubheaderSpace={subHeaderComponent == null} ignoreLateralMenu={lateralMenuComponent == null}>
                <ContainerBody id={INSIDE_BODY_CONTAINER_ID} overflow={bodyHasOverflow} padding={bodyPadding}>
                    {children}
                </ContainerBody>
            </ContentContainer>
        </Container>
    );
};
